import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { fetchSignedUrl } from '../api/client';
import { useSession } from '../app/WorkforceContext';
import { proofCaption } from '../lib/proof-photos';

/**
 * Foto bukti sebuah percobaan pengiriman.
 *
 * ADA KARENA foto sudah lama diunggah dan disimpan — `workforce_delivery_attempt_photos` sejak
 * 5 Agu 2026, dan API sudah mengirim `photoFileIds` bersama tiap percobaan — tetapi TIDAK ADA
 * satu pun layar yang menampilkannya. Bukti yang tidak dapat dilihat sama nilainya dengan bukti
 * yang tidak pernah diambil, dan kurir tetap diminta memotretnya setiap kali.
 *
 * Dua cacat pada versi pertama komponen ini, keduanya melanggar prinsipnya sendiri, keduanya
 * ditemukan review CTO 22 Agu dan diperbaiki di sini:
 *
 *   1. `Promise.all` menjadikan SATU foto gagal sebagai SEMUA gagal. Empat foto sehat ditahan
 *      dari layar karena satu saudaranya bermasalah — persis "ruang kosong sebagai tuduhan" yang
 *      komponen ini dibuat untuk mencegah. Kini `allSettled`.
 *
 *   2. URL bertanda tangan berumur 300 detik (`STORAGE_SIGNED_URL_TTL_SECONDS`), sementara
 *      `loading="lazy"` menunda pengambilan berkas sampai digulir. Pada daftar panjang yang
 *      dibaca sambil berdebat — cara layar ini justru dipakai saat sengketa — yang muncul adalah
 *      IKON GAMBAR RUSAK, bukan pesan, karena tidak ada `onError` dan keadaannya tetap "siap".
 *      Kini kedaluwarsa diperlakukan sebagai hal WAJAR: tautannya diambilkan yang baru.
 */

interface Foto {
  readonly fileId: string;
  /** `null` selama belum punya tautan yang dapat dipakai. */
  readonly url: string | null;
  /** Benar setelah pengambilan ulang pun gagal — berhenti mencoba, katakan apa adanya. */
  readonly menyerah: boolean;
}

export function ProofPhotos({
  attemptNo,
  fileIds,
}: {
  readonly attemptNo: number;
  readonly fileIds: readonly string[];
}): ReactNode {
  const session = useSession();
  const [foto, setFoto] = useState<readonly Foto[] | null>(null);
  const kunci = fileIds.join(',');

  useEffect(() => {
    if (fileIds.length === 0) return;
    let aktif = true;
    setFoto(null);
    // `allSettled`, bukan `all`: satu berkas bermasalah tidak boleh menyembunyikan saudaranya.
    void Promise.allSettled(fileIds.map((id) => fetchSignedUrl(session.auth, id))).then((hasil) => {
      if (!aktif) return;
      setFoto(
        fileIds.map((fileId, i) => {
          const r = hasil[i];
          return {
            fileId,
            url: r?.status === 'fulfilled' ? r.value.url : null,
            menyerah: r?.status !== 'fulfilled',
          };
        }),
      );
    });
    return () => {
      aktif = false;
    };
    // Kuncinya ISI daftar, bukan identitas array — `fileIds` baru tiap render.
  }, [session.auth, kunci]);

  /**
   * Satu gambar gagal dimuat: hampir selalu karena tautannya sudah lewat 300 detik.
   *
   * Itu bukan kerusakan, melainkan umur tautan yang memang pendek — jadi jawabannya menukar
   * tautan baru, bukan menampilkan galat. Percobaan ulang hanya SEKALI: bila yang baru pun gagal,
   * sebabnya bukan kedaluwarsa, dan mencoba terus hanya menghasilkan permintaan berulang tanpa
   * akhir pada layar yang dibuka memakai kuota kurir.
   */
  const segarkan = useCallback(
    (fileId: string) => {
      setFoto((s) => s?.map((f) => (f.fileId === fileId ? { ...f, url: null } : f)) ?? s);
      fetchSignedUrl(session.auth, fileId)
        .then((r) => {
          setFoto((s) => s?.map((f) => (f.fileId === fileId ? { ...f, url: r.url } : f)) ?? s);
        })
        .catch(() => {
          setFoto((s) => s?.map((f) => (f.fileId === fileId ? { ...f, menyerah: true } : f)) ?? s);
        });
    },
    [session.auth],
  );

  if (fileIds.length === 0) return null;

  if (foto === null) {
    return <p className="muted small">Memuat {fileIds.length} foto bukti…</p>;
  }

  const tampil = foto.filter((f) => !f.menyerah);
  const caption = proofCaption(fileIds.length, tampil.length);

  return (
    <>
      <p className={caption.bermasalah ? 'warn-text small' : 'muted small'}>{caption.teks}</p>
      {tampil.length > 0 ? (
        <div className="proof-strip">
          {tampil.map((f, i) =>
            f.url === null ? (
              <span key={f.fileId} className="proof-thumb proof-thumb-memuat" aria-hidden="true" />
            ) : (
              <a
                key={f.fileId}
                href={f.url}
                target="_blank"
                rel="noreferrer"
                className="proof-thumb"
              >
                {/*
                  `alt` menyebut percobaan dan urutannya: pada percobaan kedua, mengetahui foto ini
                  milik percobaan yang mana adalah seluruh gunanya.
                  `loading="lazy"` dipertahankan — kurir memakai kuotanya sendiri — dan `onError`
                  di bawah yang menutup akibat sampingnya.
                */}
                <img
                  src={f.url}
                  alt={`Bukti percobaan #${attemptNo}, foto ${i + 1} dari ${tampil.length}`}
                  loading="lazy"
                  decoding="async"
                  onError={() => segarkan(f.fileId)}
                />
              </a>
            ),
          )}
        </div>
      ) : null}
    </>
  );
}
